import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useAuth} from "../../../providers/AuthenticationProvider";
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import "./EquipmentDashboard.css";
import {
    Chart,
    ChartCategoryAxis,
    ChartCategoryAxisItem, ChartLegend, ChartSeries, ChartSeriesItem, ChartTitle
} from "@progress/kendo-react-charts";
import {pathianDataApiService} from "../../../services/PathianDataApiService";
import {EquipmentDashboardForm} from "./EquipmentDashboardForm";
import {equipmentService} from "../../../services/equipmentService";

const WEB_PAGE = 'Equipment Dashboard';

export const EquipmentDashboard = () => {
    const {id} = useParams();
    const {checkAuth, getUserType} = useAuth();

    const [detailConsumptionSeries, setDetailConsumptionSeries] = useState([]);
    const [energyConsumptionSeries, setEnergyConsumptionSeries] = useState([]);
    const [energyDifferenceSeries, setEnergyDifferenceSeries] = useState([]);

    const [equipment, setEquipment] = useState({});

    const navigate = useNavigate();

    const [energySummary , setEnergySummary] = useState(null);
    const [energySummaryTable, setEnergySummaryTable] = useState([]);

    useEffect(() => {loadEnergySummary();}, [energySummary]);
    useEffect(() => {loadEquipment(id);}, [id]);

    checkAuth(getUserType(), WEB_PAGE) && navigate('/');

    const loadEquipment = async (equipmentId) => {
        const result = await equipmentService.getEquipmentById(equipmentId);
        console.log(result);
        setEquipment(result.data);
    }

    const handleSubmit = async (formData) => {
        console.log(formData);
        setEnergySummaryTable([]);
        setEnergyConsumptionSeries([]);
        setEnergyDifferenceSeries([]);
        setDetailConsumptionSeries([]);

        const energySummaryPromise = getEnergySummary([id], formData.reportStartDate, formData.reportEndDate, new Date(`${formData.benchmarkYear}-01-01`), new Date(`${formData.benchmarkYear}-12-31`), formData.blendedElectricRate, formData.blendedThermalRate);
        await loadChartData(formData);
        await energySummaryPromise;
    }

    const getEnergySummary = useCallback(
        async (equipmentIds, startDate, endDate, benchmarkStartDate, benchmarkEndDate, electricRate, thermalRate) => {
            const result = await pathianDataApiService.getEnergySummary(equipmentIds, startDate, endDate, benchmarkStartDate, benchmarkEndDate, electricRate, thermalRate);
            setEnergySummary(result);
        }, []
    );

    const loadEnergySummary = () => {
        if (!energySummary) return;
        const tempEnergySummaryTable = [];
        tempEnergySummaryTable.push({
            title: 'Benchmark Consumption',
            electricalValue: `${energySummary.sumBenchmark.electricalLoad.value.toFixed(2)} ${energySummary.sumBenchmark.electricalLoad.unit}`,
            thermalValue: `${energySummary.sumBenchmark.thermalLoad.value.toFixed(2)} ${energySummary.sumBenchmark.thermalLoad.unit}`,
            totalValue: `${(energySummary.sumBenchmark.electricalLoad.value * 0.003412 + energySummary.sumBenchmark.thermalLoad.value * 1.038).toFixed(2)} MMBTU`
        });
        tempEnergySummaryTable.push({
            title: 'Reported Consumption',
            electricalValue: `${energySummary.sumReport.electricalLoad.value.toFixed(2)} ${energySummary.sumReport.electricalLoad.unit}`,
            thermalValue: `${energySummary.sumReport.thermalLoad.value.toFixed(2)} ${energySummary.sumReport.thermalLoad.unit}`,
            totalValue: `${(energySummary.sumReport.electricalLoad.value * 0.003412 + energySummary.sumReport.thermalLoad.value * 1.038).toFixed(2)} MMBTU`
        });
        tempEnergySummaryTable.push({
            title: 'Difference',
            electricalValue: `${energySummary.sumDifference.electricalLoad.value.toFixed(2)} ${energySummary.sumDifference.electricalLoad.unit}`,
            thermalValue: `${energySummary.sumDifference.thermalLoad.value.toFixed(2) } ${energySummary.sumDifference.thermalLoad.unit}`,
            totalValue: `${(energySummary.sumDifference.electricalLoad.value * 0.003412 + energySummary.sumDifference.thermalLoad.value * 1.038).toFixed(2)} MMBTU`
        });
        tempEnergySummaryTable.push({
            title: 'Percent Difference',
            electricalValue: `${100 * Math.abs((energySummary.sumReport.electricalLoad.value - energySummary.sumBenchmark.electricalLoad.value) / ((energySummary.sumReport.electricalLoad.value + energySummary.sumBenchmark.electricalLoad.value) / 2)).toFixed(2)}%`,
            thermalValue: `${100 * Math.abs((energySummary.sumReport.thermalLoad.value - energySummary.sumBenchmark.thermalLoad.value) / ((energySummary.sumReport.thermalLoad.value + energySummary.sumBenchmark.thermalLoad.value) / 2)).toFixed(2)}%`,
            totalValue: ``
        });
        tempEnergySummaryTable.push({
            title: 'Dollars',
            electricalValue: (energySummary.sumDifference.electricalLoad.value * energySummary.electricRate).toLocaleString('en-US', {style: 'currency', currency: 'USD'}),
            thermalValue: (energySummary.sumDifference.thermalLoad.value * energySummary.thermalRate).toLocaleString('en-US', {style: 'currency', currency: 'USD'}),
            totalValue: ((energySummary.sumDifference.electricalLoad.value * energySummary.electricRate) + (energySummary.sumDifference.thermalLoad.value * energySummary.thermalRate)).toLocaleString('en-US', {style: 'currency', currency: 'USD'})
        });
        setEnergySummaryTable(tempEnergySummaryTable);
    }

    const loadChartData = async (formData) => {
        const reportStartDate = formData.reportStartDate;
        const reportEndDate = formData.reportEndDate;
        const benchmarkStart = `${formData.benchmarkYear}-01-01`;
        const benchmarkEnd = `${formData.benchmarkYear}-12-31`;
        const axis = "temp";


        //get data for consumption and difference
        const energyResults = [];

        //TODO handle energy point selections

        for (let i = 0; i < formData.energyFunctions.length; i++) {
            const functionId = formData.energyFunctions[i].id;
            const equipmentId = formData.energyFunctions[i].equipment;
            const energyConsumptionResult = await pathianDataApiService.getCalculatedData(equipmentId, functionId, reportStartDate, reportEndDate, benchmarkStart, benchmarkEnd, axis);
            energyResults.push(energyConsumptionResult);
        }

        const energyFlatResult = energyResults.flat();

        //get data for detail charts

        const detailResults = [];

        for (let i = 0; i < formData.detailFunctions.length; i++) {
            const functionId = formData.detailFunctions[i].id;
            const detailConsumptionResult = await pathianDataApiService.getCalculatedData(id, functionId, reportStartDate, reportEndDate, benchmarkStart, benchmarkEnd, axis);
            detailResults.push(detailConsumptionResult);
        }

        if (formData.detailPoints.length > 0) {
            console.log('selected detail points');
            console.log(formData.detailPoints);
            const pointIds = formData.detailPoints.map(x => x.key);
            const detailConsumptionResult = await pathianDataApiService.getData(pointIds, reportStartDate, reportEndDate, benchmarkStart, benchmarkEnd, axis);
            detailResults.push(detailConsumptionResult);
        }

        const detailFlatResult = detailResults.flat();

        console.log('detail flat:');
        console.log(detailFlatResult);

        loadEnergyCharts(formData, energyFlatResult);
        loadDetailCharts(formData, detailFlatResult);
    }

    const loadEnergyCharts = (formData, energyResults) => {
        const energySeries = [];
        const differenceSeries = [];

        console.log("energy data");
        console.log(energyResults);

        console.log("selected energy functions");
        console.log(formData.energyFunctions);

        const reportData = energyResults
            .flatMap(x => x.report)
            .reduce((acc, cur) => {
                const existingItem = acc.find(item => item.key === cur.key);
                if (existingItem) {
                    existingItem.value += cur.value;
                } else {
                    acc.push({ key: cur.key, value: cur.value });
                }
                return acc;
            }, []);

        const benchmarkData = energyResults
            .flatMap(x => x.benchmark)
            .reduce((acc, cur) => {
                const existingItem = acc.find(item => item.key === cur.key);
                if (existingItem) {
                    existingItem.value += cur.value;
                } else {
                    acc.push({ key: cur.key, value: cur.value });
                }
                return acc;
            }, []);

        const reportDifferenceData = [];
        const benchmarkDifferenceData = [];

        benchmarkData.forEach((item) => {
            const reportItem = reportData.find(x => x.key === item.key);
            if (reportItem) {
                reportDifferenceData.push({ key: item.key, value: item.value - reportItem.value });
                benchmarkDifferenceData.push({ key: item.key, value: 0 });
            }
        });


        energySeries.push({
            name: "Reported",
            data: reportData
        });

        energySeries.push({
            name: "Benchmark",
            data: benchmarkData
        });

        differenceSeries.push({
            name: "Difference",
            data: reportDifferenceData
        });

        differenceSeries.push({
            name: "Benchmark",
            data: benchmarkDifferenceData
        });

        console.log("energy series");
        console.log(energySeries);

        console.log("difference series");
        console.log(differenceSeries);


        const normalizedSeries = normalizeDataSeries(energySeries);

        setEnergyConsumptionSeries(normalizedSeries);
        setEnergyDifferenceSeries(differenceSeries);
    }

    const loadDetailCharts = (formData, detailResults) => {
        const detailData = detailResults;

        const detailSeries = [];

        console.log("detail data");
        console.log(detailData);

        console.log("selected points");
        console.log(formData.detailPoints);

        for (let i = 0; i < detailData.length; i++) {
            const current = detailData[i];

            if (current.pointId) {

                const currentPoint = formData.detailPoints.find(x => x.key === current.pointId);
                const currentPointCode = currentPoint.code;

                detailSeries.push({
                    name: currentPointCode + "-Reported",
                    data: current.report
                })

                detailSeries.push({
                    name: currentPointCode + "-Benchmark",
                    data: current.benchmark
                })
            }

            if (current.functionId) {
                const currentFunction = formData.detailFunctions.find(x => x.key === (current.equipmentId + '-' + current.functionId));
                const currentFunctionCode = currentFunction.code;

                detailSeries.push({
                    name: currentFunctionCode + "-Reported",
                    data: current.report
                })

                detailSeries.push({
                    name: currentFunctionCode + "-Benchmark",
                    data: current.benchmark
                })
            }
        }

        console.log("detail series");
        console.log(detailSeries);


        const normalizedSeries = normalizeDataSeries(detailSeries);

        setDetailConsumptionSeries(normalizedSeries);
    }

    function normalizeDataSeries(objects) {
        // Find all unique keys across all data arrays in all objects
        const allKeys = new Set();
        objects.forEach(obj => {
            obj.data.forEach(item => {
                allKeys.add(item.key);
            });
        });

        // Convert Set to Array and sort the keys
        const sortedKeys = Array.from(allKeys).sort((a, b) => a - b);

        // Normalize each object's data array
        return objects.map(obj => {
            const keyMap = new Map(obj.data.map(item => [item.key, item.value]));
            const normalizedData = sortedKeys.map(key => ({
                key,
                value: keyMap.has(key) ? keyMap.get(key) : null
            }));

            return {
                name: obj.name,
                data: normalizedData
            };
        });
    }


    return (
        <div style={{marginTop: '2%', marginRight: '20px'}}>
            <h2>{equipment.name}</h2>
            <EquipmentDashboardForm equipmentId={id} onSubmit={handleSubmit}></EquipmentDashboardForm>
            <Grid
                data={energySummaryTable}
                pageable={false}
                sortable={false}
                total={5}
                style={{width: '50%'}}
            >
                <Column
                    cell={props => (
                        <td>
                            {props.dataItem.title}
                        </td>
                    )}
                />
                <Column
                    title={'YTD Electrical'}
                    cell={props => (
                        <td>
                            {props.dataItem.electricalValue}
                        </td>
                    )}
                />
                <Column
                    title={'YTD Thermal'}
                    cell={props => (
                        <td>
                            {props.dataItem.thermalValue}
                        </td>
                    )}
                />
                <Column
                    title={'YTD Total'}
                    cell={props => (
                        <td>
                            {props.dataItem.totalValue}
                        </td>
                    )}
                />
            </Grid>
            <div className={"charts-container"}>
                <div className={"consumption-container"}>
                    <div className={"chart"}>
                        <Chart>
                            <ChartTitle text={"Consumption"} font="20pt sans-serif"/>
                            <ChartLegend position="bottom" orientation="horizontal"/>
                            <ChartCategoryAxis>
                                <ChartCategoryAxisItem key={1} labels={{step: 5}} />
                            </ChartCategoryAxis>
                            <ChartSeries>
                                {energyConsumptionSeries.map((series,i) => {
                                    return (<ChartSeriesItem
                                        key={i}
                                        aggregate={"avg"}
                                        name={series.name}
                                        data={series.data}
                                        field={"value"}
                                        categoryField={"key"}
                                        markers={{visible: false}}
                                        type="line"/>);
                                })}
                            </ChartSeries>
                        </Chart>
                    </div>
                    <div className={"chart"}>
                        <Chart>
                            <ChartTitle text={"Difference"} font="20pt sans-serif"/>
                            <ChartLegend position="bottom" orientation="horizontal"/>
                            <ChartCategoryAxis>
                                <ChartCategoryAxisItem key={2} labels={{step: 5}}/>
                            </ChartCategoryAxis>
                            <ChartSeries>
                                {energyDifferenceSeries.map((series,i) => {
                                    return (<ChartSeriesItem
                                        key={i}
                                        aggregate={"avg"}
                                        name={series.name}
                                        data={series.data}
                                        field={"value"}
                                        categoryField={"key"}
                                        markers={{visible: false}}
                                        type="line"/>);
                                })}
                            </ChartSeries>
                        </Chart>
                    </div>
                </div>
                <div className={"detail-container"}>
                    <div className={"chart"}>
                        <Chart>
                            <ChartTitle text={"Detail"} font="20pt sans-serif"/>
                            <ChartLegend position="bottom" orientation="horizontal"/>
                            <ChartCategoryAxis>
                                <ChartCategoryAxisItem key={3} labels={{step: 5}}/>
                            </ChartCategoryAxis>
                            <ChartSeries>
                                {detailConsumptionSeries.map((series,i) => {
                                    return (<ChartSeriesItem
                                        key={i}
                                        aggregate={"avg"}
                                        name={series.name}
                                        data={series.data}
                                        field={"value"}
                                        categoryField={"key"}
                                        markers={{visible: false}}
                                        type="line"/>);
                                })}
                            </ChartSeries>
                        </Chart>
                    </div>
                </div>
            </div>
        </div>
    );
};
